import React from 'react';
import styled from 'styled-components';

import {
  createRentalVehicleBlock,
  deleteRentalVehicleBlock,
  getRentalCalendar,
} from '../../api/rental';
import * as colors from '../../util/colors';
import Layout from './Layout';
import {
  ActionButton,
  Actions,
  Card,
  CardTitle,
  DefinitionList,
  ErrorText,
  Field,
  FieldGrid,
  Input,
  Scroll,
  Select,
  State,
  SuccessText,
  Textarea,
} from './styles';

const DAY_MS = 24 * 60 * 60 * 1000;

const WINDOWS = [
  {days: 14, title: '2 недели'},
  {days: 30, title: 'Месяц'},
  {days: 60, title: '2 месяца'},
];

const BLOCK_REASONS = [
  {value: 'maintenance', title: 'Обслуживание'},
  {value: 'repair', title: 'Ремонт'},
  {value: 'transfer', title: 'Перегон'},
  {value: 'other', title: 'Другое'},
];

const STATUS_TITLES = {
  payment_pending: 'Ожидает оплаты',
  reserved: 'Бронь',
  active: 'В аренде',
  inspection: 'На осмотре',
  maintenance: 'Обслуживание',
  repair: 'Ремонт',
  transfer: 'Перегон',
  other: 'Блокировка',
};

const startOfDay = date => {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

const toInputValue = date => {
  const copy = startOfDay(date);
  const month = String(copy.getMonth() + 1).padStart(2, '0');
  const day = String(copy.getDate()).padStart(2, '0');
  return `${copy.getFullYear()}-${month}-${day}`;
};

const formatDayNumber = date => String(date.getDate()).padStart(2, '0');
const formatWeekday = date => ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'][date.getDay()];
const formatDateTime = value => new Date(value).toLocaleString('ru-RU', {
  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
});

/**
 * Занятость суток вычисляется пересечением с полуинтервалом `[начало, конец)`:
 * та же арифметика, что в ограничении базы, поэтому сетка не расходится с тем,
 * что реально разрешено забронировать. Смежные аренды при этом не сливаются:
 * день возврата одной машины — уже свободный день для следующей.
 */
function periodCoversDay(period, dayStart) {
  const dayEnd = dayStart + DAY_MS;
  return +new Date(period.starts_at) < dayEnd && +new Date(period.ends_at) > dayStart;
}

function CalendarContent({authorization}) {
  const [from, setFrom] = React.useState(() => startOfDay(new Date()));
  const [windowDays, setWindowDays] = React.useState(30);
  const [locationId, setLocationId] = React.useState('');
  const [data, setData] = React.useState(null);
  const [state, setState] = React.useState('loading');
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');
  const [selected, setSelected] = React.useState(null);
  const [blockForm, setBlockForm] = React.useState(null);

  const load = React.useCallback(async () => {
    if (!authorization) { return; }
    setState('loading');
    setError('');
    try {
      const to = new Date(+from + windowDays * DAY_MS);
      const response = await getRentalCalendar(authorization, {
        from: from.toISOString(),
        to: to.toISOString(),
        ...(locationId ? {location_id: locationId} : {}),
      });
      setData(response);
      setState('ready');
    } catch (requestError) {
      setError(requestError.message);
      setState('ready');
    }
  }, [authorization, from, windowDays, locationId]);

  React.useEffect(() => { load(); }, [load]);

  const days = React.useMemo(() => {
    const result = [];
    for (let index = 0; index < windowDays; index += 1) {
      result.push(new Date(+from + index * DAY_MS));
    }
    return result;
  }, [from, windowDays]);

  const periodsByVehicle = React.useMemo(() => {
    const map = {};
    ((data && data.periods) || []).forEach(period => {
      if (!map[period.vehicle_id]) { map[period.vehicle_id] = []; }
      map[period.vehicle_id].push(period);
    });
    return map;
  }, [data]);

  const shiftDays = amount => setFrom(current => startOfDay(new Date(+current + amount * DAY_MS)));

  const openBlockForm = (vehicle, day) => {
    setSelected(null);
    setSuccess('');
    setError('');
    setBlockForm({
      vehicle_id: vehicle.id,
      vehicle_title: `${vehicle.brand} ${vehicle.model} · ${vehicle.license_plate}`,
      starts_at: toInputValue(day),
      ends_at: toInputValue(new Date(+day + DAY_MS)),
      reason: 'maintenance',
      comment: '',
    });
  };

  const submitBlock = async () => {
    setError('');
    setSuccess('');
    const startsAt = new Date(`${blockForm.starts_at}T00:00:00`);
    const endsAt = new Date(`${blockForm.ends_at}T00:00:00`);
    if (!(endsAt > startsAt)) {
      setError('Дата снятия блокировки должна быть позже даты установки');
      return;
    }
    try {
      await createRentalVehicleBlock(authorization, {
        vehicle_id: blockForm.vehicle_id,
        starts_at: startsAt.toISOString(),
        ends_at: endsAt.toISOString(),
        reason: blockForm.reason,
        comment: blockForm.comment.trim() || null,
      });
      setSuccess('Автомобиль заблокирован на выбранные даты');
      setBlockForm(null);
      await load();
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const removeBlock = async blockId => {
    setError('');
    setSuccess('');
    try {
      await deleteRentalVehicleBlock(authorization, blockId);
      setSuccess('Блокировка снята');
      setSelected(null);
      await load();
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const vehicles = (data && data.vehicles) || [];
  const locations = (data && data.locations) || [];

  return (
    <div>
      <Card>
        <Filters>
          <Field>
            <span>Пункт выдачи</span>
            <Select value={locationId} onChange={event => setLocationId(event.target.value)}>
              <option value="">Все пункты</option>
              {locations.map(location => (
                <option key={location.id} value={location.id}>
                  {location.city} · {location.title}
                </option>
              ))}
            </Select>
          </Field>
          <Field>
            <span>Начало периода</span>
            <Input
              type="date"
              value={toInputValue(from)}
              onChange={event => {
                const next = new Date(`${event.target.value}T00:00:00`);
                if (!Number.isNaN(next.getTime())) { setFrom(startOfDay(next)); }
              }}
            />
          </Field>
          <Field>
            <span>Окно</span>
            <Select
              value={windowDays}
              onChange={event => setWindowDays(Number(event.target.value))}
            >
              {WINDOWS.map(item => (
                <option key={item.days} value={item.days}>{item.title}</option>
              ))}
            </Select>
          </Field>
          <NavButtons>
            <ActionButton onClick={() => shiftDays(-windowDays)}>← Назад</ActionButton>
            <ActionButton onClick={() => setFrom(startOfDay(new Date()))}>Сегодня</ActionButton>
            <ActionButton onClick={() => shiftDays(windowDays)}>Вперёд →</ActionButton>
          </NavButtons>
        </Filters>

        <Legend>
          <LegendItem kind="reserved">Бронь</LegendItem>
          <LegendItem kind="active">В аренде</LegendItem>
          <LegendItem kind="payment_pending">Ожидает оплаты</LegendItem>
          <LegendItem kind="block">Блокировка</LegendItem>
          <LegendItem kind="free">Свободно — клик ставит блокировку</LegendItem>
        </Legend>

        {error && <ErrorText>{error}</ErrorText>}
        {success && <SuccessText>{success}</SuccessText>}

        {state === 'loading' && <State>Загрузка…</State>}
        {state === 'ready' && vehicles.length === 0 && (
          <State>
            Автомобилей в этом пункте нет. Добавьте автомобиль и привяжите его к пункту —
            без этого календарь пустой.
          </State>
        )}

        {state === 'ready' && vehicles.length > 0 && (
          <Scroll>
            <GridTable minWidth={`${220 + windowDays * 34}px`}>
              <thead>
                <tr>
                  <th>Автомобиль</th>
                  {days.map(day => (
                    <DayHead key={+day} weekend={[0, 6].includes(day.getDay())}>
                      <span>{formatWeekday(day)}</span>
                      <strong>{formatDayNumber(day)}</strong>
                    </DayHead>
                  ))}
                </tr>
              </thead>
              <tbody>
                {vehicles.map(vehicle => (
                  <tr key={vehicle.id}>
                    <VehicleCell>
                      <strong>{vehicle.brand} {vehicle.model}</strong>
                      <div>{vehicle.license_plate}</div>
                      {!locationId && vehicle.city && <small>{vehicle.city}</small>}
                    </VehicleCell>
                    {days.map(day => {
                      const dayStart = +day;
                      const period = (periodsByVehicle[vehicle.id] || [])
                        .find(item => periodCoversDay(item, dayStart));
                      const kind = period
                        ? (period.kind === 'block' ? 'block' : period.status)
                        : 'free';
                      return (
                        <DayCell
                          key={dayStart}
                          kind={kind}
                          title={period
                            ? `${STATUS_TITLES[period.status] || period.status}`
                            : 'Свободно'}
                          onClick={() => (period
                            ? setSelected({period, vehicle})
                            : openBlockForm(vehicle, day))}
                        />
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </GridTable>
          </Scroll>
        )}
      </Card>

      {selected && (
        <Card style={{marginTop: 24}}>
          <CardTitle>
            {selected.period.kind === 'block' ? 'Блокировка' : 'Аренда'} ·{' '}
            {selected.vehicle.brand} {selected.vehicle.model}
          </CardTitle>
          <DefinitionList>
            <dt>Состояние</dt>
            <dd>{STATUS_TITLES[selected.period.status] || selected.period.status}</dd>
            <dt>Начало</dt>
            <dd>{formatDateTime(selected.period.starts_at)}</dd>
            <dt>Окончание</dt>
            <dd>{formatDateTime(selected.period.ends_at)}</dd>
            {selected.period.user_phone && (
              <React.Fragment>
                <dt>Клиент</dt>
                <dd>{selected.period.user_phone}</dd>
              </React.Fragment>
            )}
            {selected.period.comment && (
              <React.Fragment>
                <dt>Комментарий</dt>
                <dd>{selected.period.comment}</dd>
              </React.Fragment>
            )}
            {selected.period.rental_id && (
              <React.Fragment>
                <dt>Аренда</dt>
                <dd>{selected.period.rental_id}</dd>
              </React.Fragment>
            )}
          </DefinitionList>
          <Actions>
            {selected.period.block_id && (
              <ActionButton danger onClick={() => removeBlock(selected.period.block_id)}>
                Снять блокировку
              </ActionButton>
            )}
            <ActionButton onClick={() => setSelected(null)}>Закрыть</ActionButton>
          </Actions>
        </Card>
      )}

      {blockForm && (
        <Card style={{marginTop: 24}}>
          <CardTitle>Блокировка · {blockForm.vehicle_title}</CardTitle>
          <FieldGrid columns={3}>
            <Field>
              <span>С</span>
              <Input
                type="date"
                value={blockForm.starts_at}
                onChange={event => setBlockForm({...blockForm, starts_at: event.target.value})}
              />
            </Field>
            <Field>
              <span>По (не включая)</span>
              <Input
                type="date"
                value={blockForm.ends_at}
                onChange={event => setBlockForm({...blockForm, ends_at: event.target.value})}
              />
            </Field>
            <Field>
              <span>Причина</span>
              <Select
                value={blockForm.reason}
                onChange={event => setBlockForm({...blockForm, reason: event.target.value})}
              >
                {BLOCK_REASONS.map(item => (
                  <option key={item.value} value={item.value}>{item.title}</option>
                ))}
              </Select>
            </Field>
          </FieldGrid>
          <Field style={{marginTop: 16}}>
            <span>Комментарий</span>
            <Textarea
              short
              rows={2}
              value={blockForm.comment}
              onChange={event => setBlockForm({...blockForm, comment: event.target.value})}
            />
          </Field>
          <Actions>
            <ActionButton primary onClick={submitBlock}>Заблокировать</ActionButton>
            <ActionButton onClick={() => setBlockForm(null)}>Отменить</ActionButton>
          </Actions>
        </Card>
      )}
    </div>
  );
}

function Calendar() {
  return (
    <Layout
      title="Календарь занятости"
      description="Строки — автомобили пункта, столбцы — сутки. Занятость считается по интервалам, поэтому день возврата одной аренды сразу доступен следующей."
    >
      <CalendarContent />
    </Layout>
  );
}

const CELL_COLORS = {
  free: '#fff',
  block: '#e2e6ec',
  payment_pending: '#ffe9c9',
  reserved: '#cfe3ff',
  active: '#bfe6c2',
  inspection: '#e6d7f5',
};

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 20px;
`;

const NavButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
  color: ${colors.TEXT_MINOR};
  font-size: 13px;
`;

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  &::before {
    content: '';
    width: 14px;
    height: 14px;
    border: 1px solid ${colors.LINE};
    border-radius: 3px;
    background: ${props => CELL_COLORS[props.kind] || '#fff'};
  }
`;

const GridTable = styled.table`
  min-width: ${props => props.minWidth};
  border-collapse: collapse;
  table-layout: fixed;
`;

const DayHead = styled.th`
  width: 34px;
  padding: 6px 2px;
  border-bottom: 1px solid ${colors.LINE};
  color: ${props => props.weekend ? colors.ERROR : colors.TEXT_MINOR};
  background: ${colors.BG};
  font-size: 11px;
  text-align: center;
  span { display: block; }
  strong { display: block; font-size: 13px; }
`;

const VehicleCell = styled.td`
  width: 220px;
  padding: 8px 12px;
  border-bottom: 1px solid ${colors.LINE};
  color: ${colors.TEXT_MAIN};
  font-size: 13px;
  div { color: ${colors.TEXT_MINOR}; }
  small { color: ${colors.TEXT_MINOR}; }
`;

const DayCell = styled.td`
  height: 40px;
  border: 1px solid ${colors.LINE};
  background: ${props => CELL_COLORS[props.kind] || '#fff'};
  cursor: pointer;
  &:hover { outline: 2px solid ${colors.BLUE_MAIN}; outline-offset: -2px; }
`;

export default Calendar;
